"use client";

import { memo } from "react";
import {
  Badge,
  Box,
  Button,
  Clipboard,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import type {
  FeedbackCollectionLinkItem,
  FeedbackLinkStatus,
} from "../api/listFeedbackLinks";

const STATUS_COLOR_PALETTE: Record<FeedbackLinkStatus, string> = {
  active: "green",
  consumed: "gray",
  expired: "orange",
  invalid: "red",
  revoked: "red",
};

type FeedbackRequestCardProps = {
  copyableUrl?: string;
  isRevoking?: boolean;
  link: FeedbackCollectionLinkItem;
  onRevoke: (tokenId: string) => void;
};

function formatDateTime(value?: string | null) {
  if (!value) {
    return "No date";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function FeedbackRequestCard({
  copyableUrl,
  isRevoking = false,
  link,
  onRevoke,
}: FeedbackRequestCardProps) {
  const isActive = link.status === "active";
  const title = link.recipient_hint || link.request_title || "Feedback request";

  return (
    <Box
      borderWidth="1"
      borderColor="moveon.surface.border"
      borderRadius="md"
      bg="moveon.surface.elevatedBg"
      p="3"
    >
      <Stack gap="2" align="stretch">
        <HStack justify="space-between" align="start" gap="3">
          <Stack gap="1" minW="0">
            <Text fontSize="sm" fontWeight="semibold">
              {title}
            </Text>
            <Text fontSize="xs" color="moveon.text.muted">
              Created {formatDateTime(link.created_at)}
            </Text>
          </Stack>
          <Badge
            colorPalette={STATUS_COLOR_PALETTE[link.status] ?? "gray"}
            variant="surface"
          >
            {link.status}
          </Badge>
        </HStack>

        <HStack gap="2" flexWrap="wrap">
          <Badge variant="subtle" colorPalette="blue">
            {link.allow_text ? "Text" : "No text"}
          </Badge>
          <Badge variant="subtle" colorPalette="purple">
            {link.allow_voice ? "Voice" : "No voice"}
          </Badge>
          <Badge variant="subtle" colorPalette="green">
            {link.submitted_count ?? 0} submitted
          </Badge>
          <Badge variant="subtle" colorPalette="gray">
            {link.use_count ?? 0} uses
          </Badge>
        </HStack>

        <Text fontSize="xs" color="moveon.text.muted">
          Expires {formatDateTime(link.expires_at)}
        </Text>

        {copyableUrl ? (
          <HStack gap="2" align="center">
            <Input value={copyableUrl} readOnly size="sm" />
            <Clipboard.Root value={copyableUrl}>
              <Clipboard.Trigger asChild>
                <Button size="sm" variant="outline">
                  <Clipboard.Indicator />
                  Copy
                </Button>
              </Clipboard.Trigger>
            </Clipboard.Root>
          </HStack>
        ) : (
          <Text fontSize="xs" color="moveon.text.muted">
            Link URL was shown once when it was created.
          </Text>
        )}

        {isActive ? (
          <HStack justify="flex-end">
            <Button
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => onRevoke(link.token_id)}
              disabled={isRevoking}
              loading={isRevoking}
            >
              Revoke
            </Button>
          </HStack>
        ) : null}
      </Stack>
    </Box>
  );
}

export default memo(FeedbackRequestCard);
